import {
  EXCLUSIVE_SLOT_ANSWERS,
  INSIGHT_KEY_SLOT,
  type NoosMetricKey,
} from "~/utils/demandInsightValues"

/**
 * A collection-wide insight switch answered differently on one hierarchy node
 * (`DemandSetupInsightNode`). Shared by the engine and the Insights page so they agree.
 */
export type NodeInsightRow = {
  groupKey: string
  groupValue: string
  insightKey: string
  enabled: boolean
}

export type InsightNode = { groupKey: string; groupValue: string }

const rowsAt = (rows: readonly NodeInsightRow[], node: InsightNode): NodeInsightRow[] =>
  rows.filter((r) => r.groupKey === node.groupKey && r.groupValue === node.groupValue)

/** This node's own answer for one key: `undefined` when it follows the collection. */
export const nodeAnswerFor = (
  rows: readonly NodeInsightRow[],
  node: InsightNode,
  insightKey: string,
): boolean | undefined =>
  rows.find(
    (r) =>
      r.groupKey === node.groupKey &&
      r.groupValue === node.groupValue &&
      r.insightKey === insightKey,
  )?.enabled

/**
 * The collection's set with this node's rows laid over it. A variant under it may still resolve
 * differently through a deeper level ({@link effectiveKeysForVariant}).
 */
export const effectiveKeysAtNode = (
  collectionKeys: ReadonlySet<string>,
  rows: readonly NodeInsightRow[],
  node: InsightNode,
): Set<string> => {
  const keys = new Set(collectionKeys)
  for (const r of rowsAt(rows, node)) {
    if (r.enabled) keys.add(r.insightKey)
    else keys.delete(r.insightKey)
  }
  return keys
}

/**
 * The collection set, then the variant's node on each level in hierarchy order, deeper overriding
 * wider. A `""` value (uncategorised) has no node and is skipped. The variant's own accepted rows
 * are the caller's and still win.
 */
export const effectiveKeysForVariant = (
  collectionKeys: ReadonlySet<string>,
  rows: readonly NodeInsightRow[],
  levels: readonly string[],
  nodeValueOf: (levelKey: string) => string | null | undefined,
): Set<string> => {
  if (rows.length === 0) return new Set(collectionKeys)
  let keys: Set<string> = new Set(collectionKeys)
  for (const groupKey of levels) {
    const groupValue = nodeValueOf(groupKey)
    if (!groupValue) continue
    keys = effectiveKeysAtNode(keys, rows, { groupKey, groupValue })
  }
  return keys
}

/**
 * Node-level counterpart of `resolveInsightSlots`: the rows to write so enabling `insightKey`
 * silences its rivals. A rival on collection-wide needs an explicit off row; otherwise
 * `enabled: undefined` means "delete the node's row for this key".
 */
export const rivalWritesFor = (
  collectionKeys: ReadonlySet<string>,
  insightKey: NoosMetricKey,
): { insightKey: NoosMetricKey; enabled: false | undefined }[] => {
  const groups = EXCLUSIVE_SLOT_ANSWERS[INSIGHT_KEY_SLOT[insightKey]]
  if (!groups) return []
  const own = groups.find((g) => g.includes(insightKey))
  return groups
    .filter((g) => g !== own)
    .flat()
    .map((rival) => ({
      insightKey: rival,
      enabled: collectionKeys.has(rival) ? false : undefined,
    }))
}
